import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AppShell } from "@/layout/AppShell";
import { PageHeader, PageTitle } from "@/components/ui/page-header";
import { PersonForm } from "@/components/people/PersonForm";
import { SecretRevealDialog } from "@/components/admin/SecretRevealDialog";
import { usePeopleCapabilities } from "@/hooks/usePeopleCapabilities";
import { useUnits } from "@/hooks/useUnits";
import { useSession } from "@/store/session";

export function NewPersonPage({ unitId }: { unitId?: number }): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actor = useSession((state) => state.user);
  const caps = usePeopleCapabilities().data;
  const units = useUnits();
  const [secret, setSecret] = useState<{ password: string; email: string | null } | null>(null);
  const requestedUnitId =
    unitId != null && (units.data ?? []).some((unit) => unit.id === unitId) ? unitId : null;
  const scopedUnitId =
    requestedUnitId != null &&
    caps != null &&
    (caps.scope === "all" || caps.scope.includes(requestedUnitId))
      ? requestedUnitId
      : null;
  const lockedUnitId =
    caps && caps.scope !== "all" ? (scopedUnitId ?? caps.scope[0] ?? null) : null;
  const returnToContext = (): void => {
    if (scopedUnitId != null) {
      void navigate({
        to: "/units/$unitId",
        params: { unitId: String(scopedUnitId) },
        search: { tab: "employees" },
      });
    } else {
      void navigate({ to: "/people", search: { tab: "employees" } });
    }
  };

  return (
    <AppShell>
      <PageHeader>
        <PageTitle>{t("admin.users.create.title")}</PageTitle>
      </PageHeader>
      {caps?.can_create ? (
        <PersonForm
          mode="create"
          actorId={actor?.id ?? null}
          canManageProjectManager={actor?.role === "admin"}
          allowedRoles={caps.creatable_roles}
          lockedUnitId={lockedUnitId}
          initialUnitId={scopedUnitId}
          onOpenChange={() => undefined}
          onCancel={returnToContext}
          onCreated={(password, person) => {
            if (password) setSecret({ password, email: person.web_email ?? null });
            else void navigate({ to: "/people/$id", params: { id: String(person.id) } });
          }}
        />
      ) : (
        <p className="text-muted-foreground mt-6 text-sm">{t("peopleCard.readOnly")}</p>
      )}
      <SecretRevealDialog
        open={secret !== null}
        onOpenChange={(open) => {
          if (!open) returnToContext();
        }}
        title={t("admin.users.tempPasswordDialog.title")}
        subtitle={t("admin.users.tempPasswordDialog.subtitle")}
        note={secret?.email}
        secret={secret?.password ?? null}
        display="mono"
        copyLabel={t("admin.users.tempPasswordDialog.copy")}
        copiedLabel={t("admin.users.tempPasswordDialog.copied")}
        doneLabel={t("admin.users.tempPasswordDialog.done")}
        testIdPrefix="temp-password"
      />
    </AppShell>
  );
}
